import { useState } from 'react';
import type { BaseSlideProps, BuildUpContent } from './SlideLayout.types';
import { inlineMarkdown } from '@/lib/markdown';

export function BuildUpSlide({ title, content, accentColor }: BaseSlideProps) {
  const { items } = content as BuildUpContent;
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  // Parse items to separate title from description
  const parseItem = (item: string) => {
    // Format: "**Title** — Description"
    const match = item.match(/^\*\*(.+?)\*\*\s*[—-]\s*(.+)$/);
    if (match) {
      return { title: match[1], description: match[2] };
    }
    // Fallback if format doesn't match
    return { title: item, description: '' };
  };

  const toggleExpand = (idx: number) => {
    setExpandedIndex(expandedIndex === idx ? null : idx);
  };

  return (
    <div
      className="slide-content"
      style={{ '--color-accent': accentColor } as React.CSSProperties}
    >
      <h2 className="text-4xl font-serif font-bold mb-8 accent-text">
        {title}
      </h2>

      <div className="max-w-6xl mx-auto grid grid-cols-3 gap-3">
        {items.map((item, idx) => {
          const { title: itemTitle, description } = parseItem(item);
          const isExpanded = expandedIndex === idx;

          return (
            <div
              key={idx}
              className={`card cursor-pointer transition-all hover:shadow-md ${
                isExpanded ? 'accent-border-left' : ''
              }`}
              onClick={() => toggleExpand(idx)}
            >
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-2">
                {itemTitle}
              </h3>
              {isExpanded && description && (
                <p
                  className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed animate-fade-in"
                  dangerouslySetInnerHTML={{ __html: inlineMarkdown(description) }}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="text-center mt-6 text-sm text-slate-400">
        Click any item to see details
      </div>
    </div>
  );
}
